import React, { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const DialogModal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  actions,
  hideCloseButton = false,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  const actionButtons = useMemo(() => actions || [], [actions]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previousActiveElementRef.current = document.activeElement;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const dialogNode = dialogRef.current;
    const focusableElements = dialogNode?.querySelectorAll(FOCUSABLE_SELECTOR);
    const firstFocusable = focusableElements?.[0];
    (firstFocusable || dialogNode)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogNode) {
        return;
      }

      const elements = Array.from(dialogNode.querySelectorAll(FOCUSABLE_SELECTOR));
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener("keydown", handleKeyDown);
      previousActiveElementRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(26,24,22,0.55)] px-6 py-10"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className="w-full max-w-md rounded-10 border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-lg outline-none md:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2
              id={titleId}
              className="font-display text-24 font-semibold text-pl-text-primary"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className="mt-3 text-15 leading-7 text-pl-text-secondary"
              >
                {description}
              </p>
            ) : null}
          </div>

          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-pl border border-transparent p-2 text-pl-text-secondary transition-colors hover:border-pl-border hover:bg-pl-bg-subtle hover:text-pl-text-primary"
              aria-label="Close dialog"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {children ? (
          <div className="mt-5 text-14 leading-7 text-pl-text-secondary">
            {children}
          </div>
        ) : null}

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          {actionButtons.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              className={
                action.variant === "primary"
                  ? "rounded-pl bg-pl-accent px-4 py-3 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
                  : "rounded-pl border border-pl-border bg-pl-bg-elevated px-4 py-3 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
              }
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DialogModal;
